import { z } from 'zod';
import { timesheetsBillingCodeSchema, timesheetsClockCommandIdSchema, timesheetsClockBreakTypeSchema, timesheetsClockEntrySchema, timesheetsEntryKindSchema, timesheetsTimeEntryIdSchema, } from './timesheets-clock.contract.js';
export const timesheetsBreakTypeSchema = timesheetsClockBreakTypeSchema;
export const timesheetsReviewStateSchema = z.enum(['pending', 'approved', 'rejected', 'voided']);
export const timesheetsEntrySchema = timesheetsClockEntrySchema.extend({
    breakType: timesheetsBreakTypeSchema.nullable(),
    parentTimeEntryId: timesheetsTimeEntryIdSchema.nullable(),
    reviewState: timesheetsReviewStateSchema,
    approvedByMembershipId: z.string().uuid().nullable(),
    approvedAt: z.string().datetime({ offset: true }).nullable(),
    rejectedByMembershipId: z.string().uuid().nullable(),
    rejectedAt: z.string().datetime({ offset: true }).nullable(),
    rejectionNote: z.string().max(2_000).nullable(),
    voidedByMembershipId: z.string().uuid().nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    voidReason: z.string().max(2_000).nullable(),
    managerAdjustmentReason: z.string().max(2_000).nullable(),
    managerAdjustedByMembershipId: z.string().uuid().nullable(),
    managerAdjustedAt: z.string().datetime({ offset: true }).nullable(),
});
export const timesheetsEntryListQuerySchema = z.strictObject({
    membershipId: z.string().uuid().optional(),
    entryKind: timesheetsEntryKindSchema.optional(),
    reviewState: timesheetsReviewStateSchema.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    includeVoided: z.boolean().default(false),
    order: z.enum(['newest', 'oldest']).default('newest'),
    limit: z.number().int().min(1).max(500).default(200),
    offset: z.number().int().min(0).default(0),
}).superRefine((value, context) => {
    if (value.from !== undefined && value.to !== undefined &&
        new Date(value.from).getTime() > new Date(value.to).getTime()) {
        context.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'The Timesheets end boundary must not precede the start boundary.',
        });
    }
});
const manualEntryFields = {
    commandId: timesheetsClockCommandIdSchema,
    membershipId: z.string().uuid(),
    entryKind: timesheetsEntryKindSchema,
    breakType: timesheetsBreakTypeSchema.nullable().default(null),
    billingCode: timesheetsBillingCodeSchema,
    workReferenceId: z.string().uuid().nullable().default(null),
    notes: z.string().trim().max(2_000).nullable().default(null),
    clockInOccurredAt: z.string().datetime({ offset: true }),
    clockOutOccurredAt: z.string().datetime({ offset: true }),
};
function validateEntryShape(value, context) {
    if (new Date(value.clockOutOccurredAt).getTime() <= new Date(value.clockInOccurredAt).getTime()) {
        context.addIssue({ code: 'custom', path: ['clockOutOccurredAt'], message: 'Clock-out must be after clock-in.' });
    }
    if (value.entryKind === 'break') {
        if (value.breakType === null)
            context.addIssue({ code: 'custom', path: ['breakType'], message: 'Break type is required.' });
        if (value.billingCode !== 'break')
            context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Break entries use break billing.' });
        if (value.workReferenceId !== null)
            context.addIssue({ code: 'custom', path: ['workReferenceId'], message: 'Breaks cannot reference a Project.' });
    }
    else {
        if (value.billingCode === 'break')
            context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Work entries cannot use break billing.' });
        if (value.breakType !== null)
            context.addIssue({ code: 'custom', path: ['breakType'], message: 'Work entries cannot have a break type.' });
        if (value.workReferenceId === null && (value.notes?.trim().length ?? 0) === 0) {
            context.addIssue({ code: 'custom', path: ['notes'], message: 'Notes are required for shop time.' });
        }
    }
}
export const timesheetsCreateManualEntrySchema = z.strictObject(manualEntryFields)
    .superRefine(validateEntryShape);
export const timesheetsCorrectEntrySchema = z.strictObject({
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
    membershipId: z.string().uuid().optional(),
    entryKind: timesheetsEntryKindSchema.optional(),
    breakType: timesheetsBreakTypeSchema.nullable().optional(),
    billingCode: timesheetsBillingCodeSchema.optional(),
    workReferenceId: z.string().uuid().nullable().optional(),
    notes: z.string().trim().max(2_000).nullable().optional(),
    clockInOccurredAt: z.string().datetime({ offset: true }).optional(),
    clockOutOccurredAt: z.string().datetime({ offset: true }).optional(),
}).refine((value) => Object.keys(value).some((key) => key !== 'expectedRevision' && key !== 'reason'), { message: 'At least one correction field is required.' });
export const timesheetsReviewEntrySchema = z.strictObject({
    expectedRevision: z.number().int().positive(),
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().max(2_000).nullable().default(null),
}).superRefine((value, context) => {
    if (value.decision === 'reject' && (value.note?.length ?? 0) < 5) {
        context.addIssue({ code: 'custom', path: ['note'], message: 'Suggested corrections are required.' });
    }
});
export const timesheetsVoidEntrySchema = z.strictObject({
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
});
export const timesheetsStartBreakSchema = z.strictObject({
    commandId: timesheetsClockCommandIdSchema,
    timeEntryId: timesheetsTimeEntryIdSchema.optional(),
    breakType: timesheetsBreakTypeSchema.default('lunch'),
    occurredAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(2_000).nullable().default(null),
});
export const timesheetsEndBreakSchema = z.strictObject({
    commandId: timesheetsClockCommandIdSchema,
    breakEntryId: timesheetsTimeEntryIdSchema.optional(),
    occurredAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(2_000).nullable().default(null),
});
export const timesheetsCorrectionSchema = z.strictObject({
    id: z.string().uuid(),
    timeEntryId: timesheetsTimeEntryIdSchema,
    entryRevision: z.number().int().positive(),
    correctionKind: z.enum([
        'manual-create',
        'manager-correction',
        'approval',
        'rejection',
        'void',
        'lunch-split',
        'entry-split',
    ]),
    reason: z.string().min(5).max(2_000),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const timesheetsEntryResponseSchema = z.strictObject({ data: timesheetsEntrySchema });
export const timesheetsEntryCommandResponseSchema = z.strictObject({
    data: timesheetsEntrySchema,
    idempotent: z.boolean(),
});
export const timesheetsEntriesResponseSchema = z.strictObject({
    data: z.array(timesheetsEntrySchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
export const timesheetsCorrectionsResponseSchema = z.strictObject({ data: z.array(timesheetsCorrectionSchema) });
export const timesheetsBreakTransitionResponseSchema = z.strictObject({
    closedEntry: timesheetsEntrySchema,
    activeEntry: timesheetsEntrySchema,
    idempotent: z.boolean(),
});
//# sourceMappingURL=timesheets-entry.contract.js.map